/**
 * @file creditSigns.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-13
 * @modified 2026-10-13
 * @description A supplier credit's figures are POSITIVE (Jade, 13 Oct 2026:
 *              "Negative → Positive"). Every credit in purchasing.bills is
 *              stored positive and goes to Xero as an ACCPAYCREDIT with
 *              positive lines; a supplier's credit-note PDF usually prints
 *              its figures negative, and a person may type a minus by hand.
 *
 *   THE RULE. The DOCUMENT decides, not each line: when the credit's lines
 *   come to less than nothing, every figure is turned over — each line's
 *   amount, its tax and the header's net, VAT and total. A credit whose lines
 *   come to more than nothing is left as it is, so a deduction printed
 *   positive on a negative credit (a restocking fee) stays a deduction, and a
 *   deduction line on a positive credit stays negative. Then each line's
 *   QUANTITY is made positive and its sign carried by the unit price (a
 *   quantity of −2 at £50 is 2 at −£50), because a quantity is a count.
 *
 *   Bills are never put through this.
 *
 *   One rule for the screen (Add Supplier Credit, reading a PDF and leaving a
 *   figure typed with a minus) and the server (the credit save, and the PDF
 *   reader when it reads a credit note): the server's utils/creditSigns.js is
 *   GENERATED from this file by scripts/sync-shared-utils.js — edit this one
 *   and re-run it.
 */

const PENNY = 0.005;

function toNum(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[£,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function round4(n) {
  return Math.round((Number(n) || 0) * 10000) / 10000;
}

/** A line's net amount: its own line amount when it has one, else quantity × unit price. */
export function creditLineAmount(line, { qty = 'quantity', price = 'unit_amount', amount = 'line_amount' } = {}) {
  const own = amount ? toNum(line?.[amount]) : null;
  if (own !== null) return own;
  const q = toNum(line?.[qty]);
  const p = toNum(line?.[price]);
  if (q === null || p === null) return 0;
  return q * p;
}

/**
 * −1 when the lines come to less than nothing (the credit was read or typed
 * negative), else 1. With no line amounts, the header's net or total decides.
 */
export function creditDocumentSign(lines, opts = {}, header = null) {
  const list = Array.isArray(lines) ? lines : [];
  const net = list.reduce((sum, l) => sum + creditLineAmount(l, opts), 0);
  if (Math.abs(net) >= PENNY) return net < 0 ? -1 : 1;
  const h = toNum(header?.sub_total) ?? toNum(header?.total);
  return h !== null && h < 0 ? -1 : 1;
}

/** −x for a number; a string keeps its own digits (a typed "5" becomes "-5", "-5" becomes "5"). */
function turned(v) {
  if (v === null || v === undefined || v === '') return v;
  if (typeof v === 'number') return v === 0 ? 0 : -v;
  const s = String(v).trim();
  const n = toNum(s);
  if (n === null || n === 0) return v;
  return s.startsWith('-') ? s.slice(1) : `-${s}`;
}

function isNegative(v) {
  const n = toNum(v);
  return n !== null && n < 0;
}

/**
 * One line made positive under the document's sign (`factor` from
 * creditDocumentSign): turned over when the document is negative, then a
 * negative quantity made positive with its sign on the unit price.
 * Field names default to the save's (quantity, unit_amount, line_amount,
 * tax_amount); the screen's grid passes { price: 'unit_price', amount: null,
 * tax: null }. Returns the same object when nothing changes.
 */
export function positiveCreditLine(line, factor = 1, { qty = 'quantity', price = 'unit_amount', amount = 'line_amount', tax = 'tax_amount' } = {}) {
  if (!line) return line;
  const out = { ...line };
  let changed = false;
  if (factor < 0) {
    // Turn the money over: the price (not the quantity) carries it.
    if (toNum(out[price]) !== null && toNum(out[price]) !== 0) { out[price] = turned(out[price]); changed = true; }
    if (amount && toNum(out[amount]) !== null && toNum(out[amount]) !== 0) { out[amount] = turned(out[amount]); changed = true; }
    if (tax && toNum(out[tax]) !== null && toNum(out[tax]) !== 0) { out[tax] = turned(out[tax]); changed = true; }
  }
  if (isNegative(out[qty])) {
    out[qty] = turned(out[qty]);
    if (toNum(out[price]) !== null && toNum(out[price]) !== 0) out[price] = turned(out[price]);
    changed = true;
  }
  if (!changed) return line;
  // A number stays a number at the precision Xero takes it.
  if (typeof out[price] === 'number') out[price] = round4(out[price]);
  if (typeof out[qty] === 'number') out[qty] = round4(out[qty]);
  return out;
}

/**
 * A credit document as the save and the reader carry it — { line_items,
 * sub_total, total_tax, total } — with its figures positive. Other fields are
 * kept. `turnedOver` says whether it was read or typed negative.
 *
 * @returns {{ doc: object, turnedOver: boolean }}
 */
export function positiveCreditDocument(doc) {
  if (!doc || typeof doc !== 'object') return { doc, turnedOver: false };
  const lines = Array.isArray(doc.line_items) ? doc.line_items : [];
  const factor = creditDocumentSign(lines, {}, doc);
  const nextLines = lines.map(l => positiveCreditLine(l, factor));
  const out = { ...doc };
  if (Array.isArray(doc.line_items)) out.line_items = nextLines;
  if (factor < 0) {
    for (const k of ['sub_total', 'total_tax', 'total']) {
      if (toNum(out[k]) !== null && toNum(out[k]) !== 0) out[k] = turned(out[k]);
    }
  }
  const turnedOver = factor < 0 || nextLines.some((l, i) => l !== lines[i]);
  return { doc: out, turnedOver };
}

/**
 * The screen's grid lines (quantity, unit_price as typed) made positive — run
 * when the user leaves a figure, never while typing. Returns the same array
 * when nothing changes, so it can be set as state without a loop.
 */
export function positiveCreditGridLines(lines) {
  const list = Array.isArray(lines) ? lines : [];
  const fields = { qty: 'quantity', price: 'unit_price', amount: null, tax: null };
  const factor = creditDocumentSign(list, fields);
  let changed = false;
  const next = list.map((l) => {
    const p = positiveCreditLine(l, factor, fields);
    if (p !== l) changed = true;
    return p;
  });
  return changed ? next : lines;
}
